/**
 * @file Signup.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-13
 * @modified 2026-07-27
 */

import { useState, useEffect } from 'react';
import './Login.css';
import { API_URL } from './config';

function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      setLoading(false);
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    // Client-side email validation
    const trimmedEmail = email.trim().toLowerCase();
    if (trimmedEmail !== 'cam@mour.digital' && !trimmedEmail.endsWith('@excel-agri.com')) {
      setError('Only @excel-agri.com email addresses are allowed');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: trimmedEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-container svg-bg">
        <div className="login-card">
          <div className="login-header">
            <img
              src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
              alt="Excel Agri"
              className="login-logo-icon"
            />
          </div>

          <div className="success-message" style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              color: 'var(--color-primary)'
            }}>
              ✓
            </div>
            <h2 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>
              Request Submitted
            </h2>
            <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
              Your account request has been submitted for approval.
              You will receive an email with instructions once your account is approved.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => window.location.href = '/'}
                className="btn-primary"
              >
                <span className="material-symbols-rounded">arrow_back</span> Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container svg-bg">
      <div className="login-card">
        <div className="login-header">
          <img
            src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
            alt="Excel Agri"
            className="login-logo-icon"
          />
          <h2 style={{ marginTop: '10px', color: 'var(--text-primary)' }}>
            Request Account Access
          </h2>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label className="form-label">First Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="name@excel-agri.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <small style={{
              display: 'block',
              marginTop: '5px',
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}>
              Only @excel-agri.com email addresses
            </small>
          </div>

          <button
            type="submit"
            className="btn btn-primary signin-button"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Request Access'}
          </button>
        </form>

        <div className="signup-link">
          Already have an account? <a href="/">Sign in</a>
        </div>
      </div>
    </div>
  );
}

export default Signup;
